import React, { useCallback } from 'react';
import { t } from '@lingui/macro';

import { Box, Heading } from '@rover/kibble/core';
import type { ApiFrontendConfigurationRetrieve200SettingsDogSizeBuckets } from '@rover/rsdk/src/apiClient/latest/model/apiFrontendConfigurationRetrieve200SettingsDogSizeBuckets';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import useAvailablePets from '@rover/shared/js/hooks/useAvailablePets';
import type { SearchFiltersOptions } from '@rover/types/src/apiResponses/SearchOptionsResponse';
import type { Pet } from '@rover/types/src/Pet';
import { PetType } from '@rover/types/src/Pet';
import type { SearchFilters } from '@rover/types/src/SearchFilters';
import type { ServiceType } from '@rover/types/src/ServiceType';

import { SearchInterstitialUpdatePetType } from '../../Filters/Collections/Wizard/analyticsEvents';
import InlinePetCounter from '../../PetCounter/InlinePetCounter';

import PetPreferences from './PetPreferences';

const petsTitleText = (): string => t`Your pets`;
const petCounterTitleText = (): string => t`How many pets need care?`;
const petCounterSubtitleText = (): string =>
  t`Add at least one pet. This helps you find a sitter and see the most accurate pricing.`;

export type Props = {
  pets: Pet[];
  searchFilters: SearchFilters;
  dogCount: number;
  catCount: number;
  puppyCount: number;
  onChange: (filters: Partial<SearchFilters>) => void;
  serviceTypeFilterOptions: SearchFiltersOptions;
  petSizeBuckets: ApiFrontendConfigurationRetrieve200SettingsDogSizeBuckets;
  weightUnit: string;
  getServiceTypeBySlug: (arg0: string) => ServiceType | null | undefined;
  hasPets: boolean;
  isCatOnly: boolean;
};

// Helper to derive petType array from counts
const getPetTypeFromCounts = (dog: number, cat: number, puppy: number): PetType[] => {
  const types: PetType[] = [];
  if (dog > 0) types.push(PetType.DOG);
  if (puppy > 0) types.push(PetType.PUPPY);
  if (cat > 0) types.push(PetType.CAT);
  return types;
};

const PetSection = ({
  pets,
  searchFilters,
  dogCount,
  catCount,
  puppyCount,
  onChange,
  serviceTypeFilterOptions,
  petSizeBuckets,
  weightUnit,
  getServiceTypeBySlug,
  hasPets,
  isCatOnly,
}: Props): JSX.Element => {
  const availablePets = useAvailablePets(searchFilters.serviceType);

  const handlePetCountChange = useCallback(
    (field: 'dogCount' | 'catCount' | 'puppyCount') => (value: React.SetStateAction<number>) => {
      const currentCounts = { dogCount, catCount, puppyCount };
      const newValue = typeof value === 'function' ? value(currentCounts[field]) : value;

      const newCounts = { ...currentCounts, [field]: newValue };

      const petType = getPetTypeFromCounts(
        newCounts.dogCount,
        newCounts.catCount,
        newCounts.puppyCount
      );

      // Update both count and petType in a single onChange call
      onChange({ [field]: newValue, petType });
      emitAnalyticsEvent(
        new SearchInterstitialUpdatePetType({
          dog: petType.includes(PetType.DOG),
          cat: petType.includes(PetType.CAT),
          puppy: petType.includes(PetType.PUPPY),
        })
      );
    },
    [dogCount, catCount, puppyCount, onChange]
  );

  return (
    <Box pt="6x">
      <Heading size="300" mb="4x">
        {t`Pets`}
      </Heading>
      <InlinePetCounter
        pets={pets}
        dogs={dogCount}
        cats={catCount}
        puppies={puppyCount}
        availablePets={availablePets}
        triggeredFrom="Search"
        setDogs={handlePetCountChange('dogCount')}
        setPuppies={handlePetCountChange('puppyCount')}
        setCats={handlePetCountChange('catCount')}
        titleText={pets.length > 0 ? petsTitleText() : petCounterTitleText()}
        subtitleText={pets.length > 0 ? null : petCounterSubtitleText()}
        searchFilters={searchFilters}
        onPetSelectionChange={onChange}
      />
      <PetPreferences
        searchFilters={searchFilters}
        onChange={onChange}
        serviceTypeFilterOptions={serviceTypeFilterOptions}
        petSizeBuckets={petSizeBuckets}
        weightUnit={weightUnit}
        getServiceTypeBySlug={getServiceTypeBySlug}
        hasPets={hasPets}
        isCatOnly={isCatOnly}
      />
    </Box>
  );
};

export default PetSection;
